'use client';

import { useEffect, useState, useCallback } from 'react';
import { I18nContext, translate, translateRaw } from './context';
import { defaultLocale, isLocale, type Locale } from './config';

type Props = {
  initialLocale: Locale;
  children: React.ReactNode;
};

export function I18nProvider({ initialLocale, children }: Props) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    const saved = document.cookie
      .split('; ')
      .find((row) => row.startsWith('NEXT_LOCALE='))
      ?.split('=')[1];
    if (saved && isLocale(saved)) {
      setLocaleState(saved);
    } else if (initialLocale) {
      setLocaleState(initialLocale);
    }
  }, [initialLocale]);

  const setLocale = useCallback((next: Locale) => {
    document.cookie = `NEXT_LOCALE=${next};path=/;max-age=31536000;SameSite=Lax`;
    setLocaleState(next);
    // Reload to ensure all server-rendered content updates
    window.location.reload();
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) =>
      translate(locale, key, vars),
    [locale]
  );

  const raw = useCallback(
    <T,>(key: string) => translateRaw<T>(locale, key),
    [locale]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, raw }}>
      {children}
    </I18nContext.Provider>
  );
}
